import React from "react";
import { motion } from "framer-motion";

const roles = [
  { code: "ENG-01", title: "Distributed Systems Engineer", loc: "Stockholm / Remote", stack: "Rust · libp2p · Merkle-DAG" },
  { code: "ENG-02", title: "ERP Integrations Lead", loc: "Remote EU", stack: "SAP · Oracle · UBL · EDI" },
  { code: "ENG-03", title: "IoT Firmware Engineer", loc: "On-site / Hybrid", stack: "C · Rust · TPM 2.0" },
  { code: "COM-01", title: "Commercial Lead — Bond Programme", loc: "Stockholm", stack: "Structured Finance · IR" },
  { code: "DES-01", title: "Product Designer — Ledger UX", loc: "Remote", stack: "Systems · Data-dense UI" },
];

export const Careers = () => {
  return (
    <section
      id="careers"
      className="relative py-28 md:py-40 bg-ink-900"
      data-testid="careers-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-16">
          <div className="font-mono text-[11px] tracking-[0.35em] uppercase text-spectrum-green">
            ◢ Recruit
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            05 open roles
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[8vw] font-black leading-[0.88] tracking-tighter text-white"
              data-testid="careers-title"
            >
              Build the <span className="spectrum-text">ledger</span> that industry deserves.
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pl-10 flex flex-col justify-end">
            <p className="text-white/60 text-base md:text-lg font-light max-w-md mb-8 leading-relaxed">
              Cryptographers, ERP whisperers, firmware engineers and a commercial
              lead. Small team. Ambitious mandate. Convertible-bond financed —
              runway you can plan a career on.
            </p>
            <a
              href="mailto:erik@svenskainvest.com?subject=Talent%20—%20Application"
              className="inline-flex items-center gap-3 self-start bg-spectrum-green px-6 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-ink-950 hover:bg-white transition-colors"
              data-testid="careers-cta-apply"
            >
              Apply — erik@svenskainvest.com →
            </a>
          </div>
        </div>

        <ul className="border-t border-white/10" data-testid="careers-role-list">
          {roles.map((r, i) => (
            <motion.li
              key={r.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
              className="group border-b border-white/10"
              data-testid={`careers-role-${r.code.toLowerCase()}`}
            >
              <a
                href={`mailto:erik@svenskainvest.com?subject=Application%20—%20${encodeURIComponent(r.title)}`}
                className="grid grid-cols-12 gap-4 py-6 md:py-8 items-baseline group-hover:bg-white/[0.02] px-2 md:px-4 -mx-2 md:-mx-4 transition-colors"
              >
                <div className="col-span-2 md:col-span-1 font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green">
                  {r.code}
                </div>
                <div className="col-span-10 md:col-span-5 font-display text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight">
                  {r.title}
                </div>
                <div className="col-span-6 md:col-span-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                  {r.loc}
                </div>
                <div className="col-span-6 md:col-span-3 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                  <span className="truncate">{r.stack}</span>
                  <span className="text-white group-hover:text-spectrum-green transition-colors ml-4">→</span>
                </div>
              </a>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};
